import { useEffect, useMemo, useState } from "react";
import {
  buildItemCard,
  labelUrl,
  pathLabel,
  serialOfItem,
  type Item,
  type LabelKind,
  type LocationNode,
  type StockDetail,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { Barcode } from "../components/Barcode.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { ItemCardSheet } from "../components/ItemCardSheet.js";
import { PageHeader } from "../components/PageHeader.js";
import { QrCode } from "../components/QrCode.js";
import { Segmented } from "@vcnet/ui";

/**
 * Labels to stick on things: a code that opens the item or the shelf in the
 * app, with its name in words beside it. Items come one at a time or as a
 * selection (ticked on the Stock or Items screen); a location gets its own
 * plus one for everything below it, because bins are labelled in a sitting,
 * not one at a time. Printing hides the rest of the app (see `@media print`).
 * Labels are sized in inches for real stock, a sheet or a printer roll (see
 * `LABEL_STOCKS`), so they print the same whatever the screen's zoom.
 *
 * Items can also print as cards: half a Letter page each, two to a sheet with
 * a cut line between, or a whole page each, with the code, the facts and room
 * to write (see `ItemCardSheet`).
 *
 * The code holds only an address (see `labels.ts` in shared), so the label
 * says nothing about what is in the bin to anyone who is not signed in.
 */

type Format = "label" | "half" | "full";

/**
 * What the labels print on: a Letter sheet of address labels, or a roll in a
 * label printer, where each label is a page of its own at the label's size.
 * Each has a matching `label-stock-*` class and `@page` in app.css. The choice
 * lives in `localStorage`: it is the printer on this desk, not a place.
 */
const LABEL_STOCKS = [
  {
    value: "sheet",
    name: '1" × 2⅝" sheet of 30 (Avery 5160)',
    hint: "Prints on US Letter, 30 to a sheet. Set margins to Default and scale to 100%.",
  },
  {
    value: "address",
    name: '1" × 2⅝" roll',
    hint: 'One label to a page, 2⅝" × 1". In the print dialog pick the matching paper size, margins None, scale 100%.',
  },
  {
    value: "dymo",
    name: '1⅛" × 3½" roll (Dymo 30252, Brother DK-1201)',
    hint: 'One label to a page, 3½" × 1⅛". In the print dialog pick the matching paper size, margins None, scale 100%.',
  },
  {
    value: "thermal-wide",
    name: '1¼" × 2¼" roll (thermal)',
    hint: 'One label to a page, 2¼" × 1¼". In the print dialog pick the matching paper size, margins None, scale 100%.',
  },
  {
    value: "thermal-small",
    name: '1" × 2" roll (thermal)',
    hint: 'One label to a page, 2" × 1". In the print dialog pick the matching paper size, margins None, scale 100%.',
  },
] as const;

type LabelStock = (typeof LABEL_STOCKS)[number]["value"];

const STOCK_KEY = "iltocat.label-stock";

const savedStock = (): LabelStock => {
  try {
    const value = window.localStorage.getItem(STOCK_KEY);
    return LABEL_STOCKS.find((stock) => stock.value === value)?.value ?? "sheet";
  } catch {
    return "sheet";
  }
};

interface Label {
  id: string;
  kind: LabelKind;
  title: string;
  where: string;
  /** An item's serial number, printed as a linear barcode a handheld scanner reads. */
  serial?: string;
}

interface Loaded {
  item: Item;
  detail: StockDetail | null;
}

/** How many items are read at once: enough to be quick, few enough not to queue the browser behind them. */
const AT_ONCE = 6;

function descendants(node: LocationNode): LocationNode[] {
  return [node, ...node.children.flatMap(descendants)];
}

export function Labels({
  kind,
  ids,
  taxonomy,
  stock,
  onBack,
}: {
  /** "item" with one id or many (a selection); "location" with one, which brings everything below it. */
  kind: LabelKind;
  ids: string[];
  taxonomy: Taxonomy;
  /** True in a stockroom, where the card also says where the item is as stock. */
  stock: boolean;
  onBack: () => void;
}) {
  const [loaded, setLoaded] = useState<Loaded[] | null>(null);
  const [progress, setProgress] = useState(0);
  /** Items in the selection that have been deleted since it was made. */
  const [gone, setGone] = useState(0);
  const [format, setFormat] = useState<Format>("label");
  const [labelStock, setLabelStock] = useState<LabelStock>(savedStock);
  const [error, setError] = useState<string | null>(null);
  const idList = ids.join(",");

  function chooseStock(next: LabelStock) {
    setLabelStock(next);
    try {
      window.localStorage.setItem(STOCK_KEY, next);
    } catch {
      // Not remembered means asked again next time, nothing worse.
    }
  }

  useEffect(() => {
    if (kind !== "item") return;
    let live = true;
    const wanted = idList.split(",").filter(Boolean);
    setLoaded(null);
    setProgress(0);
    setGone(0);
    setError(null);

    // A 404 on the detail is an item captured in a stockroom and never put on
    // the shelf; a 404 on the item is one deleted since it was ticked.
    const one = async (id: string): Promise<Loaded | null> => {
      const notFound = (problem: unknown) => {
        if (problem instanceof ApiError && problem.status === 404) return null;
        throw problem;
      };
      const [found, detail] = await Promise.all([
        api.items.get(id).catch(notFound),
        stock ? api.stock.detail(id).catch(notFound) : Promise.resolve(null),
      ]);
      return found ? { item: found.item, detail } : null;
    };

    void (async () => {
      const results: (Loaded | null)[] = new Array(wanted.length).fill(null);
      let next = 0;
      let done = 0;
      const worker = async () => {
        while (live && next < wanted.length) {
          const at = next++;
          results[at] = await one(wanted[at]!);
          done += 1;
          if (live) setProgress(done);
        }
      };
      try {
        await Promise.all(Array.from({ length: Math.min(AT_ONCE, wanted.length) }, worker));
        if (!live) return;
        const found = results.filter((result): result is Loaded => result !== null);
        setGone(wanted.length - found.length);
        setLoaded(found);
      } catch {
        if (live)
          setError(
            wanted.length === 1 ? "That item could not be loaded." : "Those items could not be loaded.",
          );
      }
    })();

    return () => {
      live = false;
    };
  }, [kind, idList, stock]);

  const placeOf = (item: Item) => {
    const place = item.locationId ? taxonomy.byId.get(item.locationId) : undefined;
    return place ? pathLabel(place.path) : "";
  };

  const labels: Label[] | null = useMemo(() => {
    if (kind === "item") {
      return (
        loaded?.map(({ item }) => ({
          id: item.id,
          kind: "item" as const,
          title: item.title || "Untitled",
          where: placeOf(item),
          serial: serialOfItem(item),
        })) ?? null
      );
    }
    const node = taxonomy.byId.get(ids[0] ?? "");
    if (!node) return taxonomy.loading ? null : [];
    return descendants(node).map((one) => ({
      id: one.id,
      kind: "location" as const,
      title: one.name,
      where: one.path.length > 1 ? pathLabel(one.path.slice(0, -1)) : "",
    }));
  }, [kind, idList, loaded, taxonomy.byId, taxonomy.loading]);

  const cards = useMemo(
    () =>
      (loaded ?? []).map(({ item, detail }) => {
        const clientId = detail?.stock.clientId;
        return {
          item,
          card: buildItemCard({
            item,
            categoryName: taxonomy.categories.find((category) => category.id === item.categoryId)?.name ?? "",
            where: placeOf(item),
            stock: detail
              ? {
                  detail,
                  clientName:
                    detail.movements.find((movement) => movement.clientId === clientId)?.clientName ?? "",
                }
              : null,
          }),
        };
      }),
    [loaded, taxonomy.byId, taxonomy.categories],
  );

  const origin = window.location.origin;
  const asCard = kind === "item" && format !== "label";
  const count = labels?.length ?? 0;
  const thing = kind === "item" ? (count === 1 ? "item" : "items") : "shelf";
  const sheets = format === "half" ? Math.ceil(count / 2) : count;

  return (
    <>
      <PageHeader
        title="Labels"
        back={{ label: "Back", onClick: onBack }}
        meta={
          count > 0 ? (
            <span>
              {asCard
                ? count === 1
                  ? "A card for this item."
                  : `${count} cards, on ${sheets} ${sheets === 1 ? "sheet" : "sheets"}.`
                : count === 1
                  ? "1 label."
                  : `${count} labels.`}{" "}
              Each code opens its {kind === "item" ? "item" : "shelf"} in utucat once you are signed in.
            </span>
          ) : undefined
        }
        actions={
          count > 0 ? (
            <button type="button" className="button" onClick={() => window.print()}>
              <Icon name="qr" size={18} />
              <span>Print</span>
            </button>
          ) : undefined
        }
      />

      {error && <Notice tone="error">{error}</Notice>}

      {gone > 0 && (
        <Notice tone="warn">
          {gone === 1
            ? "1 of the items picked no longer exists, so it is left out."
            : `${gone} of the items picked no longer exist, so they are left out.`}
        </Notice>
      )}

      {labels === null && !error && (
        <>
          {kind === "item" && ids.length > 1 && (
            <p className="muted small">
              Reading {progress} of {ids.length}…
            </p>
          )}
          <div className="skeleton-line" />
        </>
      )}

      {labels?.length === 0 && (
        <EmptyState icon="qr" title="Nothing to label">
          {kind === "item" && ids.length > 1
            ? "None of those items exist any more."
            : `That ${thing} no longer exists.`}
        </EmptyState>
      )}

      {count > 0 && (
        <div className="label-format">
          {kind === "item" && (
            <Segmented<Format>
              label="What to print"
              value={format}
              onChange={setFormat}
              options={[
                { value: "label", label: "Label" },
                { value: "half", label: "Half page" },
                { value: "full", label: "Full page" },
              ]}
            />
          )}
          {asCard ? (
            <p className="muted small">
              {format === "half"
                ? "Two to a US Letter sheet, with a dashed line to cut along. "
                : "Prints on US Letter. "}
              Choose "Save as PDF" or your printer, with margins set to Default and scale at 100%.
            </p>
          ) : (
            <>
              <select
                className="input label-stock"
                aria-label="Label size"
                value={labelStock}
                onChange={(event) => chooseStock(event.target.value as LabelStock)}
              >
                {LABEL_STOCKS.map((stock) => (
                  <option key={stock.value} value={stock.value}>
                    {stock.name}
                  </option>
                ))}
              </select>
              <p className="muted small">{LABEL_STOCKS.find((stock) => stock.value === labelStock)?.hint}</p>
            </>
          )}
        </div>
      )}

      {asCard && count > 0 && (
        <div className="card-preview">
          {cards.map(({ item, card }) => (
            <ItemCardSheet
              key={item.id}
              card={card}
              itemId={item.id}
              coverPhotoId={item.photos[0]?.id ?? null}
              origin={origin}
              size={format === "full" ? "full" : "half"}
              kind={stock ? "stock" : "personal"}
            />
          ))}
        </div>
      )}

      {!asCard && count > 0 && (
        <ul className={`label-sheet label-stock-${labelStock}${labelStock === "sheet" ? "" : " label-roll"}`}>
          {labels!.map((label) => (
            <li key={`${label.kind}-${label.id}`} className="label">
              <QrCode text={labelUrl(origin, label.kind, label.id)} label={`Code for ${label.title}`} />
              <span className="label-text">
                <strong className="label-title">{label.title}</strong>
                {label.where && <span className="label-where">{label.where}</span>}
                {label.serial && (
                  <span className="label-serial">
                    <Barcode text={label.serial} label={`Barcode for serial number ${label.serial}`} />
                    <span className="label-where">{label.serial}</span>
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
